// src/components/shell/popovers/useRailPopover.ts
// Shared state machine for sidebar popovers:
//   • open/close + outside-click + escape
//   • fixed-position calculation anchored to the trigger button
//
// Popovers open to the right of the sidebar (rail/desktop variants) and
// fall back to anchored-below on mobile so they don't shoot off-screen
// inside the drawer.
//
// Vertical anchoring: triggers in the lower half of the viewport anchor by
// BOTTOM (panel grows upward), which keeps the panel from being cut off when
// the trigger lives at the bottom of the rail (help / settings / profile /
// impersonation). Triggers in the upper half anchor by TOP (grows downward).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export type RailPopoverPlacement = "right" | "below";

export type RailPopoverAnchor =
  | { kind: "top"; top: number }
  | { kind: "bottom"; bottom: number };

export type RailPopoverPosition = {
  left: number;
  anchor: RailPopoverAnchor;
};

export type UseRailPopoverResult = {
  open: boolean;
  setOpen: (next: boolean) => void;
  toggle: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  panelRef: React.RefObject<HTMLDivElement | null>;
  position: RailPopoverPosition;
  placement: RailPopoverPlacement;
};

const PANEL_OFFSET_PX = 8;
const PANEL_MIN_WIDTH = 280;
const VIEWPORT_MARGIN_PX = 24;
/** Triggers below this fraction of the viewport height anchor by BOTTOM (grow up). */
const LOWER_HALF_THRESHOLD = 0.55;

export function useRailPopover(): UseRailPopoverResult {
  const [open, setOpenState] = useState(false);
  const [position, setPosition] = useState<RailPopoverPosition>({
    left: 0,
    anchor: { kind: "top", top: 0 },
  });
  const [placement, setPlacement] = useState<RailPopoverPlacement>("right");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const setOpen = useCallback((next: boolean) => setOpenState(next), []);
  const toggle = useCallback(() => setOpenState((o) => !o), []);

  useLayoutEffect(() => {
    if (!open) return;
    const node = triggerRef.current;
    if (!node) return;

    const updatePos = () => {
      const rect = node.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const roomToRight = viewportWidth - rect.right;
      const useBelow = roomToRight < PANEL_MIN_WIDTH + PANEL_OFFSET_PX * 2;
      const anchorByBottom = rect.top > viewportHeight * LOWER_HALF_THRESHOLD;

      if (useBelow) {
        setPlacement("below");
        const left = Math.max(VIEWPORT_MARGIN_PX, rect.left);
        if (anchorByBottom) {
          // Trigger is low — pop UP, panel's bottom sits above the trigger.
          setPosition({
            left,
            anchor: { kind: "bottom", bottom: Math.max(0, viewportHeight - rect.top + PANEL_OFFSET_PX) },
          });
        } else {
          setPosition({
            left,
            anchor: { kind: "top", top: rect.bottom + PANEL_OFFSET_PX },
          });
        }
        return;
      }

      setPlacement("right");
      const left = rect.right + PANEL_OFFSET_PX;
      if (anchorByBottom) {
        // Anchor panel bottom to trigger bottom so it grows upward alongside the rail.
        setPosition({
          left,
          anchor: { kind: "bottom", bottom: Math.max(0, viewportHeight - rect.bottom) },
        });
      } else {
        setPosition({ left, anchor: { kind: "top", top: rect.top } });
      }
    };

    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpenState(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenState(false);
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return { open, setOpen, toggle, triggerRef, panelRef, position, placement };
}

/**
 * Below this viewport width, the popover renders as a near-full-screen sheet
 * instead of an anchored panel. Rationale: on a 360x644 phone the dev
 * simulator + settings + help popovers stack ~600px of vertical content; the
 * anchored panel maxHeight calc + mobile browser chrome leave the X close
 * button just barely out of reach. Sheet mode pins the sticky header X at
 * the visible top edge of the viewport (`100dvh` so iOS Safari's collapsing
 * URL bar doesn't eat the bottom). Dev sync note 59d3b4d8.
 */
const NARROW_VIEWPORT_PX = 640;
const NARROW_SHEET_INSET_PX = 8;

/** Standard inline styles applied to the floating panel; combine with rounded/shadow Tailwind classes.
 *
 * z-index note (dev sync c238ce65): `DevViewportFrame` uses z-[9000] for its
 * dark backdrop when the mobile-preview simulator is active. Rail popovers
 * (impersonation, settings, help, etc.) need to sit ABOVE that frame so a
 * dev who turned the simulator on from the impersonation popover can also
 * turn it back off — without z>9000 here, the popover paints behind the
 * frame backdrop and becomes uninteractable. 9200 leaves headroom for any
 * future modal that needs to layer above popovers but still under the
 * frame's escape hatch. Firefly tour highlights sit above this at
 * `FIREFLY_TOUR_*` in `src/lib/firefly/fireflyTourLayerZIndex.ts`.
 */
export function railPopoverPanelStyle(position: RailPopoverPosition): React.CSSProperties {
  const isNarrowViewport =
    typeof window !== "undefined" && window.innerWidth < NARROW_VIEWPORT_PX;
  if (isNarrowViewport) {
    // Content-height sheet: pin top + left/right, omit bottom so short help
    // copy does not stretch full-page. Tall Settings still scrolls via maxHeight.
    // Dev-sync 89e19fdc.
    return {
      position: "fixed",
      left: NARROW_SHEET_INSET_PX,
      right: NARROW_SHEET_INSET_PX,
      top: NARROW_SHEET_INSET_PX,
      maxHeight: `calc(100dvh - ${NARROW_SHEET_INSET_PX * 2}px)`,
      overflowY: "auto",
      zIndex: 9200,
    };
  }
  const base: React.CSSProperties = {
    position: "fixed",
    left: position.left,
    minWidth: PANEL_MIN_WIDTH,
    maxWidth: `min(420px, calc(100vw - ${VIEWPORT_MARGIN_PX * 2}px))`,
    overflowY: "auto",
    zIndex: 9200,
  };
  if (position.anchor.kind === "top") {
    return {
      ...base,
      top: position.anchor.top,
      maxHeight: `calc(100dvh - ${position.anchor.top + VIEWPORT_MARGIN_PX}px)`,
    };
  }
  return {
    ...base,
    bottom: position.anchor.bottom,
    maxHeight: `calc(100dvh - ${position.anchor.bottom + VIEWPORT_MARGIN_PX}px)`,
  };
}
